/**
 * Pure layer-range math shared by the harness (Phase A), the p2p driver
 * (Phase B), and eventually legion's stagePlanner (Phase C).
 */
/**
 * Split `totalLayers` into `stages` contiguous ranges, sizes as even as
 * possible with earlier stages taking the remainder (matches Skippy's
 * even-contiguous plan).
 */
export function splitLayerRanges(totalLayers, stages) {
    if (!Number.isInteger(totalLayers) || totalLayers <= 0) {
        throw new RangeError(`totalLayers must be a positive integer, got ${totalLayers}`);
    }
    if (!Number.isInteger(stages) || stages <= 0 || stages > totalLayers) {
        throw new RangeError(`stages must be in [1, totalLayers], got ${stages}`);
    }
    const base = Math.floor(totalLayers / stages);
    const remainder = totalLayers % stages;
    const ranges = [];
    let start = 0;
    for (let i = 0; i < stages; i++) {
        const size = base + (i < remainder ? 1 : 0);
        ranges.push({ layerStart: start, layerEnd: start + size });
        start += size;
    }
    return ranges;
}
/**
 * Split `totalLayers` proportionally to per-stage weights (e.g. VRAM bytes),
 * guaranteeing every stage gets at least one layer. Skippy's
 * plan_weighted_contiguous analog.
 */
export function splitLayerRangesWeighted(totalLayers, weights) {
    if (weights.length === 0)
        throw new RangeError('weights must be non-empty');
    if (weights.some((w) => !(w > 0) || !Number.isFinite(w))) {
        throw new RangeError('weights must be positive finite numbers');
    }
    if (weights.length > totalLayers) {
        throw new RangeError(`more stages (${weights.length}) than layers (${totalLayers})`);
    }
    const total = weights.reduce((a, b) => a + b, 0);
    // Largest-remainder apportionment with a floor of 1 layer per stage.
    const ideal = weights.map((w) => (w / total) * totalLayers);
    const counts = ideal.map((x) => Math.max(1, Math.floor(x)));
    let assigned = counts.reduce((a, b) => a + b, 0);
    const byRemainder = ideal
        .map((x, i) => ({ i, frac: x - Math.floor(x) }))
        .sort((a, b) => b.frac - a.frac);
    let cursor = 0;
    while (assigned < totalLayers) {
        counts[byRemainder[cursor % byRemainder.length].i]++;
        assigned++;
        cursor++;
    }
    cursor = 0;
    while (assigned > totalLayers) {
        const idx = byRemainder[byRemainder.length - 1 - (cursor % byRemainder.length)].i;
        if (counts[idx] > 1) {
            counts[idx]--;
            assigned--;
        }
        cursor++;
    }
    const ranges = [];
    let start = 0;
    for (const size of counts) {
        ranges.push({ layerStart: start, layerEnd: start + size });
        start += size;
    }
    return ranges;
}
export function isFirstStage(d) {
    return d.layerStart === 0;
}
export function isFinalStage(d) {
    return d.layerEnd === d.totalLayers;
}
export const BYTES_PER_ELEMENT = {
    f32: 4,
    f16: 2,
    bf16: 2,
};
/** Expected activation payload size in bytes. */
export function activationBytes(tokenCount, nEmbd, dtype) {
    if (!Number.isInteger(tokenCount) || tokenCount <= 0) {
        throw new RangeError(`tokenCount must be a positive integer, got ${tokenCount}`);
    }
    if (!Number.isInteger(nEmbd) || nEmbd <= 0) {
        throw new RangeError(`nEmbd must be a positive integer, got ${nEmbd}`);
    }
    return tokenCount * nEmbd * BYTES_PER_ELEMENT[dtype];
}
/**
 * Estimated KV-cache bytes for ONE session covering layers [layerStart,
 * layerEnd) at `ctxSize` tokens of context (M1: N sessions on one loaded
 * stage each pay this cost independently — the model weights themselves
 * are shared, not duplicated; see native/stage_glue.cpp's multi-session
 * API). `kvBytesPerTokenPerLayer` is n_kv_heads × head_dim × bytesPerElement
 * for ONE of K or V, assumed uniform across the stage's layers (the
 * manifest's optional kv_bytes_per_token_per_layer field — see
 * manifest.ts); the leading factor of 2 accounts for storing both K and V:
 * perSessionKvBytes ≈ 2 × ctx × Σ_stage_layers(n_kv_heads × head_dim) × bytesPerElement.
 */
export function perSessionKvBytes(ctxSize, layerStart, layerEnd, kvBytesPerTokenPerLayer) {
    if (!Number.isInteger(ctxSize) || ctxSize <= 0) {
        throw new RangeError(`ctxSize must be a positive integer, got ${ctxSize}`);
    }
    if (!Number.isInteger(layerStart) || layerStart < 0) {
        throw new RangeError(`layerStart must be a non-negative integer, got ${layerStart}`);
    }
    if (!Number.isInteger(layerEnd) || layerEnd <= layerStart) {
        throw new RangeError(`layerEnd must be an integer > layerStart, got ${layerEnd}`);
    }
    if (!(kvBytesPerTokenPerLayer > 0) || !Number.isFinite(kvBytesPerTokenPerLayer)) {
        throw new RangeError(`kvBytesPerTokenPerLayer must be a positive finite number, got ${kvBytesPerTokenPerLayer}`);
    }
    const numLayers = layerEnd - layerStart;
    return 2 * ctxSize * numLayers * kvBytesPerTokenPerLayer;
}
/** Validate a StageDescriptor; returns a list of problems (empty = valid). */
export function validateStageDescriptor(d) {
    const problems = [];
    if (!d.modelId)
        problems.push('modelId is required');
    if (!Number.isInteger(d.layerStart) || d.layerStart < 0)
        problems.push('layerStart must be a non-negative integer');
    if (!Number.isInteger(d.layerEnd) || d.layerEnd <= d.layerStart)
        problems.push('layerEnd must be > layerStart');
    if (!Number.isInteger(d.totalLayers) || d.totalLayers <= 0 || d.totalLayers < d.layerEnd)
        problems.push('totalLayers must be a positive integer >= layerEnd');
    if (d.shardUrls.length === 0)
        problems.push('shardUrls must be non-empty');
    if (!Number.isInteger(d.ctxSize) || d.ctxSize <= 0)
        problems.push('ctxSize must be a positive integer');
    return problems;
}
